import { motion } from "framer-motion";
import { ArrowRight, Hourglass } from "lucide-react";
import { KAJABI, STUDIO_BG } from "@/lib/data";

const curriculum = [
  {
    title: "Songwriter DNA",
    body: "Discover how you're naturally wired as a creator. Learn whether you're a Tour Guide or an Explorer, how that impacts your writing process, and how understanding your creative identity can help you write stronger songs and collaborate more effectively.",
  },
  {
    title: "Writing to Brief and Winning",
    body: "Learn how to interpret real-world sync briefs, understand what clients are truly asking for, avoid common mistakes, and develop a process for responding quickly without sacrificing creativity.",
  },
  {
    title: "Emotionally Connected Songwriting",
    body: "The songs that win don't just fit a brief, they make people feel something. Learn frameworks for creating authentic emotional connection through lyric, melody, perspective, tension, anticipation, and storytelling.",
  },
  {
    title: "Artistry in Today's Sync Market",
    body: "Discover why artistry matters more than ever. Learn how music supervisors, brands, and publishers are increasingly seeking authentic artist-driven music, and how to develop a recognizable creative identity that stands out.",
  },
  {
    title: "Premium Songs vs. Library Songs",
    body: "Not all sync music is built for the same opportunities. Learn the difference between premium songs and traditional production music, where each fits in the marketplace, and how to identify which lane best supports your goals.",
  },
  {
    title: "Inside Production for Sync",
    body: "Go behind the scenes with professional producers as they break down the production decisions that help songs compete in today's sync marketplace. From arrangement and sonic choices to emotional impact and commercial readiness.",
  },
  {
    title: "Essential Sync Contracts",
    body: "Understand the agreements that shape a sync career. Learn the practical differences between publishing deals, co-publishing agreements, administration deals, master representation agreements, and licensing contracts so you can move forward with confidence.",
  },
  {
    title: "The Long Game: Mindset, Strategy & Career Development",
    body: "An in-depth conversation with Becki and Devon about what actually creates sustainable success in sync. Learn how to think like a professional, avoid common traps, build momentum, navigate setbacks, and create a career that lasts.",
  },
];

export default function Course() {
 return (
 <div data-testid="course-page">
 <section
 data-testid="course-hero"
 className="relative overflow-hidden grain border-b border-ink-600"
 >
 <div
 className="absolute inset-0 bg-cover bg-center opacity-40"
 style={{ backgroundImage: `url(${STUDIO_BG})` }}
 />
 <div className="absolute inset-0 bg-gradient-to-b from-ink-900/80 via-ink-900/90 to-ink-900" />

 <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-36 md:pt-44 pb-20">
 <div className="flex flex-col lg:flex-row lg:items-center gap-10">
 <motion.div
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6 }}
 className="flex-1 max-w-3xl"
 >
 <div className="inline-flex items-center gap-2 bg-amber/10 border border-amber/40 text-amber font-mono text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm">
 <Hourglass size={12} />
 8-Week Live Immersion · Coming Soon
 </div>
 <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.1] tracking-normal mt-6">
 The <span className="text-amber italic">SYNC</span> Immersion.
 </h1>
 <p className="mt-8 text-xl md:text-2xl text-white/80 max-w-3xl leading-snug">
 8 weeks. 8 mini-courses. One full-immersion experience that
 actually embeds you in the real world of sync. With a community
 standing next to you while it happens.
 </p>
 </motion.div>

 <motion.div
 initial={{ opacity: 0, scale: 0.92 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ duration: 0.9, ease: "easeOut" }}
 data-testid="course-hero-logo"
 className="hidden lg:flex relative shrink-0 w-[24rem] xl:w-[28rem] aspect-square items-center justify-center"
 >
 <div className="absolute inset-0 bg-gradient-radial from-amber/10 to-transparent blur-3xl pointer-events-none" />
 <img
 src="/sync-immersion-logo.png"
 alt="The SYNC Immersion - Underground Mix"
 className="relative w-full h-full object-contain drop-shadow-[0_0_60px_rgba(34,197,94,0.2)]"
 />
 </motion.div>
 </div>
 </div>
 </section>

 {/* Body */}
 <section
 data-testid="course-body"
 className="relative bg-ink-900 py-24 md:py-32"
 >
 <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-14">
 <div className="lg:col-span-7">
 <p className="tag-kicker">What you'll actually walk away with</p>
 <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight mt-3">
 Not another sync 101.{" "}
 <span className="text-amber italic">The integration step.</span>
 </h2>
 <p className="mt-6 text-white/70 text-lg leading-relaxed">
 Most sync education stops at definitions and theory. The SYNC
 Immersion picks up where that ends. And walks you all the way
 to a real catalog, real pipeline, and a real pitch rhythm.
 Weekly live sessions. Eight foundational mini-courses. Real
 community around you the entire time.
 </p>

 <ul className="mt-10 space-y-3">
 {curriculum.map((c, i) => (
 <li
 key={i}
 data-testid={`course-item-${i}`}
 className="flex items-start gap-4 p-5 border border-ink-600 bg-ink-800"
 >
 <span className="text-amber font-mono text-sm mt-0.5 shrink-0">
 {String(i + 1).padStart(2, "0")}
 </span>
 <div className="min-w-0">
 <p className="font-display text-base md:text-lg font-bold text-white leading-snug">
 {c.title}
 </p>
 <p className="mt-2 text-sm text-white/70 leading-relaxed">
 {c.body}
 </p>
 </div>
 </li>
 ))}
 </ul>

 <div className="mt-10 grid sm:grid-cols-2 gap-4">
 <div className="p-5 border border-ink-600 bg-ink-800 text-center">
 <p className="font-display text-3xl font-bold text-amber">8</p>
 <p className="text-[10px] font-mono uppercase tracking-widest text-white/55 mt-1">
 Weekly live meetings
 </p>
 </div>
 <div className="p-5 border border-ink-600 bg-ink-800 text-center">
 <p className="font-display text-3xl font-bold text-amber">8</p>
 <p className="text-[10px] font-mono uppercase tracking-widest text-white/55 mt-1">
 Mini-courses
 </p>
 </div>
 </div>
 </div>

 <div className="lg:col-span-5">
 <div
 data-testid="course-cta-card"
 className="sticky top-28 relative overflow-hidden border border-amber/40 bg-gradient-to-br from-ink-800 to-ink-900 p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(34,197,94,0.2)]"
 >
 <div className="absolute inset-0 bg-gradient-to-br from-amber/10 to-transparent pointer-events-none" />
 <div className="relative">
 <p className="tag-kicker">Free Webinar · September 15</p>
 <h3 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight">
 Get in the <span className="text-amber italic">room</span>{" "}
 first.
 </h3>
 <p className="mt-4 text-white/65 leading-relaxed">
 Join our free live webinar on{" "}
 <span className="text-white font-semibold">
 September 15th
 </span>{" "}
 for a first look at the SYNC Immersion. Spots are limited.
 </p>

 <a
 href={KAJABI.immersion}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="course-waitlist-cta"
 className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-sm px-6 py-5 rounded-sm transition-all group"
 >
 Register Now
 <ArrowRight
 size={16}
 className="transition-transform group-hover:translate-x-1"
 />
 </a>

 <p className="mt-4 text-xs text-amber font-mono uppercase tracking-widest text-center">
 Sept 15 · Free live webinar · Spots limited
 </p>
 </div>
 </div>
 </div>
 </div>
 </section>
 </div>
 );
}
